import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

import { useComposableFilter } from '@/components/ComposableFilter';
import Icon from '@/components/Icon/Icon';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import {
  CostOverviewFilterBar,
  type CostOverviewViewMode,
  type ComparisonTarget,
} from '@/features/contract-and-cost/cost';
import { EditBottomSheet } from '@/features/contract-and-cost/EditBottomSheet';
import {
  getMonthsInPeriod,
  parsePeriodStart,
} from '@/features/contract-and-cost/period';
import { CostStatCards } from '@/features/device/contract-and-cost/components/CostStatCards';
import { CostSummaryChart } from '@/features/device/contract-and-cost/components/CostSummaryChart';
import {
  MonthlyEditGrid,
  type MonthlyEditGridHandle,
} from '@/features/device/contract-and-cost/components/MonthlyEditGrid';
import { DeviceContractType } from '@/features/device/contract-and-cost/components/MonthlyEditGrid/types';
import { handleApiError } from '@/lib/api/error';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';

import { CostOverviewTable } from './-components/CostOverviewTable';
import { useSaveDeviceCostMonthly } from './-mutations';
import {
  useDeviceCostCatalogDetail,
  useDeviceCostCatalogOverview,
} from './-query';

const SearchSchema = z.object({
  viewMode: z.enum(['monthly', 'transition']).default('transition'),
  month: z.number().min(1).max(12).optional(),
  freeText: z.string().optional(),
  pageIndex: z.number().default(0),
  pageSize: z.number().default(100),
  comparisonTarget: z
    .enum(['PREVIOUS_YEAR', 'PREVIOUS_MONTH'])
    .default('PREVIOUS_MONTH'),
});

export const Route = createFileRoute(
  '/_staff/device-catalog/active/$id/_layout/contract-and-cost/_contract-and-cost/cost/$period/',
)({
  component: CostOverviewPage,
  validateSearch: zodValidator(SearchSchema),
});

function CostOverviewPage() {
  const { id, period } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const { t, i18n } = useTranslation(defaultNS, {
    keyPrefix: 'features.contractAndCost.costOverview',
  });
  const { t: tCommon } = useTranslation(defaultNS, {
    keyPrefix: 'features.contractAndCost',
  });
  const { t: tCostTable } = useTranslation(defaultNS, {
    keyPrefix: 'pages.devices.contractAndCost.costOverview.table',
  });

  const isMonthly = search.viewMode === 'monthly';
  const [editOpen, setEditOpen] = useState(false);
  const [costEditGridKey, setCostEditGridKey] = useState(0);
  const monthOptions = useMemo(() => getMonthsInPeriod(period), [period]);

  const { toast } = useToast();

  const costEditGridRef = useRef<MonthlyEditGridHandle>(null);
  const summaryRegionId = 'cost-overview-summary-region';
  const contracts = useMemo(
    () =>
      Object.values(DeviceContractType).map((type) => ({
        id: type,
        label: tCostTable(`contractType.${type}`),
      })),
    [tCostTable],
  );

  const saveCostMutation = useSaveDeviceCostMonthly(id, monthOptions, {
    onSuccess: () => {
      toast({ title: tCommon('editBottomSheet.saveSuccessToast') });
      setEditOpen(false);
    },
    onError: (error) => {
      const { message } = handleApiError(error, i18n);
      toast({ title: message, variant: 'destructive' });
    },
  });

  const currentMonth = search.month ?? new Date().getMonth() + 1;

  const [showSummary, setShowSummary] = useState(false);

  const filter = useComposableFilter<Record<string, never>>({
    filterDefs: [],
    filterState: {},
    onFilterStateChange: () => {},
  });

  const parsedPeriodStart = parsePeriodStart(period);
  const periodDate = new Date(
    Date.UTC(parsedPeriodStart.year, parsedPeriodStart.month - 1, 1),
  );

  const { data: summary } = useDeviceCostCatalogOverview(id, {
    targetYearMonth: periodDate.toISOString(),
  });
  const transitionPurchaseQuery = useDeviceCostCatalogDetail(id, {
    targetYearMonth: periodDate.toISOString(),
    costComparisonTarget: search.comparisonTarget,
    contractType: 'PURCHASE',
  });
  const transitionLeaseQuery = useDeviceCostCatalogDetail(id, {
    targetYearMonth: periodDate.toISOString(),
    costComparisonTarget: search.comparisonTarget,
    contractType: 'LEASE',
  });

  const handleViewModeChange = useCallback(
    (mode: CostOverviewViewMode) => {
      navigate({
        search: (prev) => ({
          ...prev,
          viewMode: mode,
          month: mode === 'monthly' ? currentMonth : undefined,
        }),
        replace: true,
      });
    },
    [navigate, currentMonth],
  );

  const handleMonthChange = useCallback(
    (month: number) => {
      navigate({
        search: (prev) => ({ ...prev, month }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleComparisonTargetChange = useCallback(
    (target: ComparisonTarget) => {
      navigate({
        search: (prev) => ({ ...prev, comparisonTarget: target }),
      });
    },
    [navigate],
  );

  return (
    <div className="relative flex flex-1 flex-col gap-5 overflow-auto px-12 py-5 min-h-0">
      <div id={summaryRegionId} hidden={!showSummary}>
        {summary && (
          <VStack className="gap-5">
            <CostStatCards summary={summary} />
            <CostSummaryChart data={summary.months} months={monthOptions} />
          </VStack>
        )}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          className="flex items-center gap-1 transition-colors"
          aria-expanded={showSummary}
          aria-controls={summaryRegionId}
          onClick={() => setShowSummary((prev) => !prev)}
        >
          <Icon
            iconName={showSummary ? 'ChevronUpIcon' : 'ChevronDownIcon'}
            className={cn('size-3.5')}
          />
          <Text
            className={cn(
              'text-labelSmallReg',
              'text-neutral-muted-on-fill',
              'hover:text-neutral-on-fill',
            )}
          >
            {showSummary ? t('summary.collapse') : t('summary.expand')}
          </Text>
        </button>
      </div>

      <CostOverviewFilterBar
        viewMode={search.viewMode}
        onViewModeChange={handleViewModeChange}
        selectedMonth={isMonthly ? currentMonth : undefined}
        onSelectedMonthChange={handleMonthChange}
        monthOptions={monthOptions}
        filter={filter}
        comparisonTarget={search.comparisonTarget}
        onComparisonTargetChange={handleComparisonTargetChange}
      />

      <HStack className="justify-end">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 text-labelSmall"
          onClick={() => {
            setCostEditGridKey((k) => k + 1);
            setEditOpen(true);
          }}
        >
          <Icon iconName="PencilIcon" className="size-3.5" />
          {tCommon('editButton')}
        </Button>
      </HStack>

      <EditBottomSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        title={tCommon('editBottomSheet.editCostTitle')}
        onSave={() => {
          const rows = costEditGridRef.current?.getAllContractData();
          if (!rows?.length) return;
          saveCostMutation.mutate(rows);
        }}
        onCancel={() => setEditOpen(false)}
      >
        <MonthlyEditGrid
          key={costEditGridKey}
          ref={costEditGridRef}
          months={monthOptions}
          contracts={contracts}
          dataList={[transitionPurchaseQuery.data, transitionLeaseQuery.data]
            .filter((data) => data != null)
            .map((data) => ({
              contractType: data.contractType,
              months: data.months.map((month) => ({
                targetYearMonth: month.targetYearMonth,
                plannedAmount: month.plannedCostAmount,
                actualAmount: month.actualCostAmount,
                quantity: month.quantity,
              })),
            }))}
        />
      </EditBottomSheet>

      {!isMonthly && (
        <CostOverviewTable
          purchaseData={transitionPurchaseQuery.data}
          leaseData={transitionLeaseQuery.data}
          isPending={
            transitionPurchaseQuery.isPending || transitionLeaseQuery.isPending
          }
          months={monthOptions}
          className="flex-1 min-h-0"
        />
      )}
    </div>
  );
}
